import {
  formatDatetime2,
  formatTransactionAmount,
} from "123tcs-common/utils/formatters";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../contexts/Language";
import SimulationTooltip from "../SimulationTooltip";

/**
 * The Transactions component show the transaction history of a card.
 * @param {Object} props All the properties of the component.
 * @returns The Transactions component.
 */
function Transactions(props) {
  const { card, showEndOfLife } = props;

  // Contexts
  const { languageCode } = useLanguage();
  const { t: translate } = useTranslation();

  // Functions
  const getTransactionAmountTd = (transactionAmount) => {
    let tdClass = "neutral";

    if (transactionAmount.startsWith("+")) {
      tdClass = "positive";
    }

    if (transactionAmount.startsWith("-")) {
      tdClass = "negative";
    }

    return (
      <SimulationTooltip title={`td.${tdClass} span`}>
        <td className={`${tdClass} bedrag`} style={{ textAlign: "right" }}>
          <span>{transactionAmount}</span>
        </td>
      </SimulationTooltip>
    );
  };

  /**
   * Renders a single transaction row for the table. Does not render end of life transactions
   * if props.showEndOfLife is false.
   * @param {Object} transaction The transactions
   * @param {Number} rowKey The row key used to identify the row.
   * @returns A html formatted table row containing the transaction.
   */
  const renderTransaction = (transaction, rowKey) => {
    if (showEndOfLife || transaction.command !== "retire") {
      return (
        <tr key={rowKey}>
          <SimulationTooltip title="td.datum span">
            <td className="datum">
              <span>{formatDatetime2(languageCode, transaction.date)}</span>
            </td>
          </SimulationTooltip>

          <SimulationTooltip title="td.acties span">
            <td className="acties">
              <span>{translate(`commands.${transaction.command}`)}</span>
            </td>
          </SimulationTooltip>

          {getTransactionAmountTd(
            formatTransactionAmount(
              transaction.amount,
              transaction.currency,
              transaction.command,
            ),
          )}

          <SimulationTooltip title="td.retailernaam span">
            <td className="retailernaam">
              <span>{transaction.retailer.name}</span>
            </td>
          </SimulationTooltip>

          <SimulationTooltip title="td.plaats span">
            <td className="plaats">
              <span>{transaction.retailer.city}</span>
            </td>
          </SimulationTooltip>
        </tr>
      );
    }

    return null;
  };

  // Render
  return (
    <table id="transactions" className="bottom-table">
      {/* TABLE HEADER */}
      <thead>
        <tr>
          <SimulationTooltip title="th.datum span">
            <th className="datum">
              <span>{translate("date")}</span>
            </th>
          </SimulationTooltip>

          <SimulationTooltip title="th.acties span">
            <th className="acties">
              <span>{translate("command")}</span>
            </th>
          </SimulationTooltip>

          <SimulationTooltip title="th.bedrag span">
            <th className="bedrag" style={{ textAlign: "right" }}>
              <span>{translate("amount")}</span>
            </th>
          </SimulationTooltip>

          <SimulationTooltip title="th.retailernaam span">
            <th className="retailernaam">
              <span>{translate("retailerName")}</span>
            </th>
          </SimulationTooltip>

          <SimulationTooltip title="th.plaats span">
            <th className="plaats">
              <span>{translate("place")}</span>
            </th>
          </SimulationTooltip>
        </tr>
      </thead>

      {/* TABLE BODY */}
      <tbody>
        {card.transactions.map((transaction, rowKey) =>
          renderTransaction(transaction, rowKey),
        )}
      </tbody>
    </table>
  );
}

Transactions.propTypes = {
  card: PropTypes.shape({
    transactions: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  showEndOfLife: PropTypes.bool,
};

Transactions.defaultProps = {
  showEndOfLife: false,
};

export default Transactions;
